import {AnyAction} from "redux";
import * as _ from "lodash";
import {REMOVE_POWER_SOURCE, ADD_POWER_SOURCE_AT_LOCATION, REMOVE_ALL_POWER_SOURCES, RESET_ALL_POWER_SOURCES, REMOVE_ALL_POWER_SOURCE_TYPE, REMOVE_ALL_DEPRECATED_STATIONS, REMOVE_STATIONS} from "../actions/action";
import {getDefaultPowerSourcesData, IPowerSource, IPowerSourceLocation, IYear} from "../../data/data";
import {createSelector} from "reselect";
import {getSelectedLocation, getSelectedLocationData} from "./location";
import {PowerSourceCounts} from './savedPowerSourceCounts';
import {getDeprecate, getDeprecations} from './power-sources/deprecate';
import {getYearData} from './year';

//  define selectors

export const getPowerSourceID = (state, props: { id: string }) => props.id;
export const getPowerSourceRequirements = (state, props: { power_source: IPowerSource }) => props.power_source.requirements;
export const getPowerSources = state => state.powerSources;
export const getPopulatedPowerSources = state => _.compact(state.powerSources);
export const getLocationID = (state, props: { location_id: number }) => props.location_id;


/**
 * count the number of power sources for all types
 */
export const countPowerSources = createSelector(

    [getPowerSources],

    (power_sources: IPowerSource[]) : number =>
    {
        return _.size(_.compact(power_sources));
    }
);


/**
 * count the number of power sources for all types
 */
export const countPowerSourcesByType = createSelector(

    [getPowerSources, getYearData, getDeprecations],

    (power_sources: IPowerSource[], year: IYear, deprecations: boolean[]) : PowerSourceCounts =>
    {
        // console.group('countPowerSourcesByType');
        // console.log('counts', _.countBy(_.compact(power_sources), 'id'));
        // console.groupEnd();

        const counts: PowerSourceCounts = _.countBy(_.compact(power_sources), 'id');

        counts['wind-land'] += counts['wind-sea'];

        const deprecate_id: string = year.deprecate;

        if (deprecate_id)
        {
            const deprecated_count: number = _.filter(deprecations, value => value).length;

            counts[deprecate_id] -= deprecated_count;
        }

        return counts;
    }
);


/**
 * count the number of power sources of a given type
 */
export const countPowerSourcesByID = createSelector(

    [countPowerSourcesByType, getPowerSourceID],

    (counts: { [id: string]: number }, power_source_id: string) : number =>
    {
        return counts[power_source_id];
    }
);


/**
 * get the power source at a given location
 */
export const getPowerSourceAtLocation = createSelector(

    [getPowerSources, getLocationID],

    (power_sources: IPowerSource[], location_id: number) =>
    {
        return power_sources[location_id];
    }
);


/**
 * check whether a power source is eligible to be placed at a location
 */
export const isPowerSourceEligible = createSelector(

    [getPowerSourceRequirements, getSelectedLocationData],

    (requirements: string[], location: IPowerSourceLocation) : boolean=>
    {
        return _.isEmpty(requirements) || _.includes(requirements, location.type);
    }
);


/**
 * get the power source at the selected location
 */
export const getPowerSourceAtSelectedLocation = createSelector(

    [getPowerSources, getSelectedLocation],

    (power_sources: IPowerSource[], location_id: number) =>
    {
        return power_sources[location_id];
    }
);


/**
 * get the power source at the selected location
 */
export const isPowerSourceLastOfType = createSelector(

    [countPowerSourcesByType, getPowerSourceAtLocation],

    (counts: PowerSourceCounts, power_source: IPowerSource) =>
    {
        if (! power_source) return false;

        //  ignore coal and gas; these can all be removed from the map

        if (power_source.id === 'coal' || power_source.id === 'gas') return false;

        //  fudge the counts for wind land/sea

        const wind_land: number = counts['wind-land'] - counts['wind-sea'];

        if (power_source.id === 'wind-land') return power_source && wind_land <= 1;

        return power_source && counts[power_source.id] <= 1;
    }
);





// /**
//  * check whether the power source at a location has been disabled by an event
//  */
// export const isPowerSourceDisabled = createSelector(
//
//     [getPowerSourceAtLocation, getOutageAffectedPowerSources],
//
//     (power_source: IPowerSource, affects: string) : boolean =>
//     {
//         if (! power_source || ! affects) return false;
//
//         return affects === power_source.id;
//     }
// );
//
//
// /**
//  * check whether the power source at the selected location has been disabled by an event
//  */
// export const isSelectedPowerSourceDisabled = createSelector(
//
//     [getPowerSourceAtSelectedLocation, getOutageAffectedPowerSources],
//
//     (power_source: IPowerSource, affects: string) : boolean =>
//     {
//         // console.group('isSelectedPowerSourceDisabled');
//         // console.log(power_source);
//         // console.log(affects);
//         // console.groupEnd();
//
//         if (! power_source || ! affects) return false;
//
//         return affects === power_source.id;
//     }
// );


//  define the initial state

const initialState: IPowerSource[] = _.fill(Array(160), undefined);


/**
 * reducer: set the current modal ID
 * @param {number} state
 * @param {AnyAction} action
 * @returns {number}
 */
export function powerSources(state = initialState, action: AnyAction) : typeof initialState
{
    switch (action.type)
    {
        case ADD_POWER_SOURCE_AT_LOCATION:
        {
            const {payload} = action;
            const {power_source, location_id} = payload;

            // console.group('powerSources: SET_SELECTED_POWER_SOURCE');
            // console.log('state', state);
            // console.log(power_source);
            // console.log(location_id);
            // console.groupEnd();

            return [
                ...state.slice(0, location_id),
                power_source,
                ...state.slice(location_id + 1)
            ];
        }

        case REMOVE_POWER_SOURCE:
        {
            const {payload} = action;
            const {location_id} = payload;

            return [
                ...state.slice(0, location_id),
                null,
                ...state.slice(location_id + 1)
            ];
        }

        case REMOVE_ALL_POWER_SOURCES:
        {
            return _.fill(Array(50), undefined);
        }

        case REMOVE_ALL_POWER_SOURCE_TYPE:
        {
            const {payload} = action;
            const {source_type} = payload;

            return _.map(state, (power_source: IPowerSource) => power_source && power_source.id === source_type ? undefined : power_source);
        }

        case RESET_ALL_POWER_SOURCES:
        {
            const base_state: IPowerSource[] = initialState.slice(0);

            return _.merge(base_state, getDefaultPowerSourcesData());
        }

        // case REMOVE_ALL_DEPRECATED_STATIONS:
        // {
        //     const {payload} = action;
        //     const {deprecate_type} = payload;
        //
        //     return state.map((power_source: IPowerSource) => power_source && power_source.id === deprecate_type ? undefined : power_source);
        // }

        case REMOVE_STATIONS:
        {
            const {payload} = action;
            const {stations} = payload;

            return state.map((power_source: IPowerSource, id: number) => stations[id] ? undefined : power_source);
        }

        default:

            return state;
    }
}



// WEBPACK FOOTER //
// ./src/ts/store/reducers/powerSources.ts